import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Header, Icon, Message, Modal, Segment, Table, TextArea } from 'semantic-ui-react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'
import { SimpleTable, TableToolbar } from 'components/SimpleTable'
import { createQueryRequest, fetchAllQueryRequests, getQueryRequests, recoverQueryRequest, resolveQueryRequest } from '../../store/actions/queries_request'

const newQuery = () => ({ id: `${Date.now()}-${Math.random()}`, value: '' })
const queryList = (item) => {
	if (Array.isArray(item?.queries)) return item.queries
	if (typeof item?.query_text === 'string') {
		try {
			const parsed = JSON.parse(item.query_text)
			if (Array.isArray(parsed)) return parsed
			if (typeof parsed === 'string') return [parsed]
		} catch (error) { /* query_text puede ser una sentencia antigua sin serializar */ }
	}
	return [item?.query_text || '']
}
const summaryOf = (item) => {
	if (!item?.summary_result) return null
	if (typeof item.summary_result === 'object') return item.summary_result
	try { return JSON.parse(item.summary_result) } catch { return null }
}
const resultOf = (item) => {
	if (item.error_text) return item.error_text
	const results = summaryOf(item)?.queries
	if (!Array.isArray(results)) return '—'
	return results.map((result) => {
		const prefix = `#${result.index} ${result.type}`
		if (result.affected_rows !== undefined) return `${prefix}: ${result.affected_rows} fila(s) afectada(s)`
		const preview = Array.isArray(result.preview) ? result.preview : []
		return `${prefix}: ${result.rows || 0} fila(s)${result.truncated ? ' (vista previa limitada)' : ''}${preview.length ? '' : ' sin filas'}`
	}).join(' | ')
}
const dateOf = (value) => value ? new Date(value).toLocaleString('es-MX') : '—'

  
const QueryDetailsModal = ({ request, onClose, t }) => {
	if (!request) return null
	const queries = queryList(request)
	const results = Array.isArray(summaryOf(request)?.queries) ? summaryOf(request).queries : []
	const resultFor = (index) => results.find((result) => result.index === index + 1) || null

	return <Modal open={!!request} onClose={onClose} size='large'>
		<Modal.Header>{t('Detalle de solicitud')} #{request.id}</Modal.Header>
		<Modal.Content scrolling>
			<Table definition celled compact><Table.Body>
				<Table.Row><Table.Cell>{t('Estado')}</Table.Cell><Table.Cell>{request.state || '—'}</Table.Cell></Table.Row>
				<Table.Row><Table.Cell>{t('Solicitante')}</Table.Cell><Table.Cell>{request.applicant_user || '—'}</Table.Cell></Table.Row>
				<Table.Row><Table.Cell>{t('Supervisor')}</Table.Cell><Table.Cell>{request.approver_user || '—'}</Table.Cell></Table.Row>
				<Table.Row><Table.Cell>{t('Fecha de envio')}</Table.Cell><Table.Cell>{dateOf(request.createdAt)}</Table.Cell></Table.Row>
				<Table.Row><Table.Cell>{t('Fecha de revision')}</Table.Cell><Table.Cell>{dateOf(request.reviewedAt)}</Table.Cell></Table.Row>
				<Table.Row><Table.Cell>{t('Fecha de ejecucion')}</Table.Cell><Table.Cell>{dateOf(request.executedAt)}</Table.Cell></Table.Row>
				<Table.Row><Table.Cell>{t('Sentencias en el lote')}</Table.Cell><Table.Cell>{queries.length}</Table.Cell></Table.Row>
			</Table.Body></Table>
			{request.reject_reason && <Message warning>{request.reject_reason}</Message>}
			{request.error_text && <Message negative>{request.error_text}</Message>}
			<Header as='h3'>{t('Sentencias y resultados')}</Header>
			{queries.map((query, index) => {
				const result = resultFor(index)
				const preview = Array.isArray(result?.preview) ? result.preview : []
				const resultColumns = result?.columns || Object.keys(preview[0] || {})
				return <Segment key={`${request.id}-${index}`}>
					<Header as='h4'>{t('Sentencia')} #{index + 1} {result?.type && <small>({result.type})</small>}</Header>
					<pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f7f7f7', padding: 8 }}>{query}</pre>
					{!result ? <Message info>{request.state === 'EJECUTADA' ? t('No hay resultado almacenado para esta sentencia.') : t('Esta solicitud todavia no se ha ejecutado.')}</Message> : result.affected_rows !== undefined ? <p><strong>{t('Filas afectadas')}:</strong> {result.affected_rows}</p> : <>
						<p><strong>{t('Filas devueltas')}:</strong> {result.rows || 0} {result.truncated && <strong>({t('vista previa limitada')})</strong>}</p>
						{preview.length > 0 ? <div style={{ overflowX: 'auto' }}>
							<Table celled compact>
								<Table.Header>
									<Table.Row>{resultColumns.map((column) => 
										<Table.HeaderCell key={column}>{column}</Table.HeaderCell>
										)}
									</Table.Row>
							</Table.Header>
							<Table.Body>{preview.map((row, rowIndex) => 
								<Table.Row key={rowIndex}>{resultColumns.map((column) => 
									<Table.Cell key={column}>{typeof row[column] === 'object' ? JSON.stringify(row[column]) : String(row[column] ?? '')}
									</Table.Cell>)}
								</Table.Row>)}
							</Table.Body>
						</Table>
					</div> : <p>{t('Sin filas')}</p>}
					</>}
				</Segment>
			})}
		</Modal.Content>
		<Modal.Actions><Button onClick={onClose}>{t('Cerrar')}</Button></Modal.Actions>
	</Modal>
}

const exportRowsForRequests = (requests) => requests.flatMap((request) => {
	const queries = queryList(request)
	const results = Array.isArray(summaryOf(request)?.queries) ? summaryOf(request).queries : []
	const base = {
		'ID solicitud': request.id,
		'Estado': request.state,
		'Solicitante': request.applicant_user,
		'Supervisor': request.approver_user || '',
		'Fecha': request.createdAt,
	}

	if (!results.length) return [{
		...base,
		'Índice sentencia': '',
		'Tipo': request.query_type || '',
		'Query SQL': queries.join('\n\n'),
		'Filas devueltas': '',
		'Filas afectadas': '',
		'Truncado': '',
		'Datos resultado': request.error_text || 'Sin resultado',
	}]

	return results.flatMap((result) => {
		const query = queries[(result.index || 1) - 1] || ''
		const resultBase = {
			...base,
			'Índice sentencia': result.index,
			'Tipo': result.type,
			'Query SQL': query,
			'Filas devueltas': result.rows ?? '',
			'Filas afectadas': result.affected_rows ?? '',
			'Truncado': result.truncated ? 'Sí' : 'No',
		}
		const preview = Array.isArray(result.preview) ? result.preview : []
		if (!preview.length) return [{ ...resultBase, 'Datos resultado': 'Sin filas' }]
		return preview.map((row) => ({ ...resultBase, 'Datos resultado': JSON.stringify(row) }))
	})
})

const downloadRequestsExcel = (requests, filename) => {
	const rows = exportRowsForRequests(requests)
	const worksheet = XLSX.utils.json_to_sheet(rows)
	const workbook = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Queries')
	XLSX.writeFile(workbook, filename)
}

const QueryExecutorPage = ({ associate, createRequest, loadRequests, loadAllRequests, resolveRequest, recoverRequest }) => {
	const { t } = useTranslation()
	const [queries, setQueries] = useState([newQuery()])
	const [history, setHistory] = useState([])
	const [loading, setLoading] = useState(true)
	const [sending, setSending] = useState(false)
	const [activeAction, setActiveAction] = useState(null)
	const [selectedRequest, setSelectedRequest] = useState(null)
	const [exporting, setExporting] = useState(false)
	const [rejectId, setRejectId] = useState(null)
	const [rejectReason, setRejectReason] = useState('')
	const [search, setSearch] = useState('')
	const [filterValues, setFilterValues] = useState({})
	const [page, setPage] = useState(1)
	const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0, per_page: 50 })
	const PAGE_SIZE = 50
	const isSupervisor = !!(associate?.supervisor || associate?.is_supervisor || associate?.super_admin)

	const buildLoadParams = useCallback((searchValue = '', filters = {}) => {
		const params = { q: searchValue.trim() || undefined }
		const filterFields = { status: 'state', requester: 'applicant_user', supervisor: 'approver_user' }
		Object.entries(filters).forEach(([key, values]) => {
			if (Array.isArray(values) && values.length) params[`filter_${filterFields[key] || key}`] = values.join(',')
		})
		return params
	}, [])
	const refresh = useCallback(async (searchValue = '', pageNumber = 1, filters = {}) => {
		setLoading(true)
		try {
			const response = await loadRequests({ page: pageNumber, limit: PAGE_SIZE, ...buildLoadParams(searchValue, filters) })
			setHistory(response?.items || [])
			setPagination(response?.pagination || { current_page: pageNumber, total_pages: 1, total_records: 0, per_page: PAGE_SIZE })
		} catch (error) { toast.error(error?.message || t('No se pudo cargar el historial'))
		} finally { setLoading(false) }
	}, [buildLoadParams, loadRequests, t])
	useEffect(() => { refresh() }, [refresh])
	useEffect(() => {
		if (!selectedRequest) return
		const updatedRequest = history.find(item => item.id === selectedRequest.id)
		if (updatedRequest && updatedRequest !== selectedRequest) setSelectedRequest(updatedRequest)
	}, [history, selectedRequest])

	const tableRows = useMemo(() => history.map((item, index) => {
		const status = String(item.state || '').toUpperCase()
		return {
			_rowKey: item.id || index,
			_request: item,
			id: item.id ? `#${item.id}` : '—',
			query: queryList(item).join('\n\n'),
			status,
			requester: item.applicant_user || '—',
			supervisor: item.approver_user || '—',
			created_at: dateOf(item.createdAt),
			result: resultOf(item),
		}
	}), [history])
	const columns = useMemo(() => [
		{ Header: t('ID'), accessor: 'id', width: 60, filterable: false },
		{ Header: t('Query'), accessor: 'query', width: 390, filterable: false },
		{ Header: t('Estado'), accessor: 'status', width: 110, filterable: true, color: (row) => ({ PENDIENTE: '#fff3cd', EJECUTANDO: '#dbeafe', EJECUTADA: '#dcfce7', APROBADA: '#dbeafe', RECHAZADA: '#fee2e2', ERROR: '#fee2e2' }[row.status]) },
		{ Header: t('Solicitante'), accessor: 'requester', width: 140, filterable: true },
		{ Header: t('Supervisor'), accessor: 'supervisor', width: 140, filterable: true },
		{ Header: t('Fecha'), accessor: 'created_at', width: 150, filterable: false },
		{ Header: t('Resultado'), accessor: 'result', width: 220, filterable: false },
	], [t])

	const sendBatch = async () => {
		const values = queries.map((item) => item.value.trim()).filter(Boolean)
		if (!values.length) return toast.error(t('Agrega al menos una sentencia SQL'))
		setSending(true)
		try { await createRequest(values); toast.success(t('Solicitud enviada a aprobación')); setQueries([newQuery()]); setPage(1); refresh(search, 1, filterValues)
		} catch (error) { toast.error(error?.message || t('No se pudo enviar la solicitud'))
		} finally { setSending(false) }
	}
	const runAction = async (id, decision, reason = '') => {
		setActiveAction(id)
		try {
			if (decision === 'RECOVER') await recoverRequest(id)
			else await resolveRequest(id, decision, reason)
			toast.success(t('Solicitud actualizada')); setRejectId(null); setRejectReason(''); refresh(search, page, filterValues)
		} catch (error) { toast.error(error?.message || t('No se pudo actualizar la solicitud'))
		} finally { setActiveAction(null) }
	}
	const handlePageChange = (newPage) => {
		setPage(newPage)
		refresh(search, newPage, filterValues)
	}
	const handleSearchChange = (value) => {
		setSearch(value)
		setPage(1)
		refresh(value, 1, filterValues)
	}
	const handleFilterChange = (accessor, values) => {
		const nextFilters = { ...filterValues }
		if (!accessor || !values || !values.length) {
			if (accessor) delete nextFilters[accessor]
			else Object.keys(nextFilters).forEach((key) => delete nextFilters[key])
		} else {
			nextFilters[accessor] = values
		}
		setFilterValues(nextFilters)
		setPage(1)
		refresh(search, 1, nextFilters)
	}
	const handleExportRequest = (request) => {
		downloadRequestsExcel([request], `query-solicitud-${request.id}.xlsx`)
	}
	const handleExportAll = async () => {
		setExporting(true)
		try {
			const requests = await loadAllRequests({ limit: PAGE_SIZE, ...buildLoadParams(search, filterValues) })
			downloadRequestsExcel(requests, `queries-busqueda-${new Date().toISOString().slice(0, 10)}.xlsx`)
			toast.success(t('Búsqueda exportada correctamente'))
		} catch (error) { toast.error(error?.message || t('No se pudo exportar la búsqueda'))
		} finally { setExporting(false) }
	}
	return <div style={{ padding: 24, maxWidth: 1500, margin: '0 auto' }}>
		<Header as='h4'>
			<Icon name='terminal' color='blue' />
			<Header.Content>{t('Ejecutor de Queries')}
				<Header.Subheader>
					{t('Solicitudes SQL con aprobación y auditoría')}
				</Header.Subheader>
			</Header.Content>
		</Header>
		<Message color='blue' icon><Icon name='info circle' />
			<Message.Content>
				<Message.Header>{t('Regla de seguridad')}</Message.Header>
				{t('Puedes enviar una o varias sentencias. El lote se valida y debe ser aprobado antes de ejecutarse.')}
			</Message.Content>
		</Message>
		<Segment>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
				<Header as='h5' style={{ margin: 0 }}>
					<Icon name='edit' />{t('Nueva solicitud')}
				</Header>
				<small>{t('Agrega una fila por sentencia SQL')}</small>
			</div>
			<div style={{ overflowX: 'auto', maxHeight: 360 }}>
				<Table celled compact unstackable>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell width={1}>#</Table.HeaderCell>
							<Table.HeaderCell>{t('Sentencia SQL')}</Table.HeaderCell>
							<Table.HeaderCell width={2}>{t('Acción')}</Table.HeaderCell>
						</Table.Row>
					</Table.Header>
					<Table.Body>{queries.map((item, index) => <Table.Row key={item.id}>
						<Table.Cell textAlign='center'><strong>{index + 1}</strong></Table.Cell>
						<Table.Cell>
							<TextArea value={item.value} onChange={(event) => setQueries((items) => items.map((row) => row.id === item.id ? { ...row, value: event.target.value } : row))} placeholder='SELECT, UPDATE, DELETE, CREATE ...' style={{ width: '100%', minHeight: 42, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }} />
						</Table.Cell>
						<Table.Cell textAlign='center'>
							<Button basic color='red' size='small' disabled={queries.length === 1} onClick={() => setQueries((items) => items.filter((row) => row.id !== item.id))}>
								{t('Quitar')}
							</Button>
						</Table.Cell>
					</Table.Row>)}</Table.Body>
				</Table></div>
			<div style={{ textAlign: 'center', margin: 10 }}>
				<Button circular basic icon='plus' 
					disabled={queries.length >= 50}
					onClick={() => setQueries((items) => [...items, newQuery()])}
					title={t('Agregar query')} />
			</div>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
				<small>{t('Las sentencias se validan y ejecutan en el orden mostrado.')}</small>
				<Button primary icon labelPosition='left' loading={sending} disabled={sending} onClick={sendBatch}>
					<Icon name='send' />{t('Enviar lote a aprobación')}
				</Button>
			</div>
		</Segment>
		<Segment>
			<TableToolbar
				title={t('Historial de solicitudes')}
				onRefresh={() => refresh(search, pagination.current_page || page, filterValues)}
				extraActions={[{
					label: exporting ? t('Exportando...') : t('Exportar Excel'),
					icon: 'file excel',
					disabled: exporting || !pagination.total_records,
					action: handleExportAll,
				}]}
			/>
			<SimpleTable 
				tableId='query-executor-history'
				loading={loading} rows={tableRows}
				columns={columns} serverSide={true}
				searchValue={search}
				onSearchChange={handleSearchChange}
				searchPlaceholder={t('Buscar en el historial...')}
				filterValues={filterValues}
				onFilterChange={handleFilterChange}
				pagination={{ currentPage: pagination.current_page || page, totalPages: pagination.total_pages || 1, totalRecords: pagination.total_records || 0 }}
				onPageChange={handlePageChange} emptyMessage={t('No hay solicitudes registradas.')}
				customActions={(row) => { 
					const request = row._request;
					const status = row.status;
					return <>
						<Button icon='eye' color='blue' size='mini' title={t('Ver detalle')} onClick={() => setSelectedRequest(request)} />
						<Button icon='file excel' color='green' size='mini' title={t('Exportar solicitud')} onClick={() => handleExportRequest(request)} />{isSupervisor && status === 'PENDIENTE' && <>
						<Button icon='check' color='green' size='mini' title={t('Aprobar')} loading={activeAction === request.id} onClick={() => runAction(request.id, 'APROBAR')} />
						<Button icon='times' color='red' size='mini' title={t('Rechazar')} disabled={!!activeAction} onClick={() => setRejectId(request.id)} /></>}
						{isSupervisor && status === 'EJECUTANDO' && <Button icon='warning sign' color='orange' size='mini' title={t('Marcar error')} loading={activeAction === request.id} onClick={() => runAction(request.id, 'RECOVER')} />}</> }} />
		</Segment>
		<QueryDetailsModal request={selectedRequest} onClose={() => setSelectedRequest(null)} t={t} />
		<Modal open={!!rejectId} onClose={() => setRejectId(null)} size='small'>
			<Modal.Header>{t('Motivo del rechazo')}</Modal.Header>
			<Modal.Content>
				<p>{t('Explica por qué no debe ejecutarse esta solicitud.')}</p>
				<TextArea autoFocus value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} style={{ width: '100%', minHeight: 110 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={() => setRejectId(null)}>{t('Cancelar')}</Button>
				<Button negative disabled={!rejectReason.trim() || !!activeAction} loading={!!activeAction} onClick={() => runAction(rejectId, 'RECHAZAR', rejectReason.trim())}>{t('Rechazar solicitud')}</Button>
			</Modal.Actions>
		</Modal>
	</div>
}

const mapStateToProps = (state) => ({ associate: state.app?.superAdmin?.associate })
const mapDispatchToProps = (dispatch) => ({ loadRequests: (params) => dispatch(getQueryRequests(params)), loadAllRequests: (params) => dispatch(fetchAllQueryRequests(params)), createRequest: (queries) => dispatch(createQueryRequest(queries)), resolveRequest: (id, decision, reason) => dispatch(resolveQueryRequest(id, decision, reason)), recoverRequest: (id) => dispatch(recoverQueryRequest(id)) })
export default connect(mapStateToProps, mapDispatchToProps)(QueryExecutorPage)
